'use client'

import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@brew/ui/dropdown-menu'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import {
  HandoffCheckIcon,
  HandoffCopyIcon,
  HandoffExternalIcon,
} from '@/components/mcp/handoff-icons'
import { McpClientLogo } from '@/components/settings/mcp/mcp-client-logo'
import { HANDOFF_HARNESSES } from '@/lib/mcp/handoff'
import { appToast } from '@/lib/shared/toast'

export type HandoffMenuContentProps = {
  /** The compact handoff prompt (built by `lib/mcp/handoff.ts`). */
  prompt: string
  /** Where "Set up MCP" points — in-app default; marketing passes `/mcp`. */
  setupHref?: string
  /** Heading above the copy + harness actions. */
  label?: string
  /** Hide for prompts that can run through the public HTTP/SDK surface. */
  showConnectorFooter?: boolean
}

/**
 * The body of a handoff dropdown — Copy prompt + Open-in-X (Claude/ChatGPT/Cursor)
 * + a setup link. Rendered inside a `<DropdownMenuContent>` so the caller
 * controls placement (`align`/`side`). `AgentHandoffButton` is the single visual
 * owner; this component keeps the menu body independently focused.
 */
export function HandoffMenuContent({
  prompt,
  setupHref = '/settings/mcp',
  label = 'Continue in your agent',
  showConnectorFooter = true,
}: HandoffMenuContentProps) {
  const [isCopied, setIsCopied] = useState(false)
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )

  useEffect(() => () => clearTimeout(copiedTimer.current), [])

  const copy = async () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
      appToast.error('Clipboard unavailable here — open in an agent instead')
      return
    }
    try {
      await navigator.clipboard.writeText(prompt)
      setIsCopied(true)
      clearTimeout(copiedTimer.current)
      copiedTimer.current = setTimeout(() => setIsCopied(false), 2000)
      appToast.success('Prompt copied')
    } catch {
      appToast.error('Could not copy the prompt')
    }
  }

  return (
    <>
      <DropdownMenuLabel className="font-medium text-caption text-muted-foreground uppercase">
        {label}
      </DropdownMenuLabel>

      <DropdownMenuItem
        onSelect={(event) => {
          event.preventDefault()
          void copy()
        }}
        className="gap-2 py-1 text-sm"
      >
        {isCopied ? (
          <HandoffCheckIcon className="size-3.5 text-emerald-600 dark:text-emerald-400" />
        ) : (
          <HandoffCopyIcon className="size-3.5 text-muted-foreground" />
        )}
        <span>{isCopied ? 'Copied' : 'Copy prompt'}</span>
        <span className="ml-auto text-caption text-muted-foreground">
          any agent
        </span>
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      {HANDOFF_HARNESSES.map((harness) => (
        <DropdownMenuItem
          key={harness.id}
          asChild
          className="gap-2 py-1 text-sm"
        >
          <a
            href={harness.buildUrl(prompt)}
            // App-scheme links (claude://) must dispatch from the current tab.
            {...(harness.isAppScheme
              ? {}
              : { target: '_blank', rel: 'noopener noreferrer' })}
          >
            <McpClientLogo
              domain={harness.logoDomain}
              name={harness.name}
              className="size-3.5 rounded"
            />
            <span>Open in {harness.name}</span>
            <HandoffExternalIcon className="ml-auto size-3 text-muted-foreground" />
          </a>
        </DropdownMenuItem>
      ))}

      {showConnectorFooter ? (
        <>
          <DropdownMenuSeparator />
          <div className="px-2 py-1.5">
            <Link
              href={setupHref}
              className="text-caption text-foreground underline"
            >
              Set up MCP
            </Link>
          </div>
        </>
      ) : null}
    </>
  )
}
